import React, { useState, useEffect } from 'react'
import { User, MapPin, Calendar, CheckCircle, CreditCard, Briefcase, Star, TrendingUp, Award, X, Edit, Camera, Shield, PhoneCall, Mail } from 'lucide-react'
import { API_BASE_URL } from '../config';
import Avatar from "./ui/Avatar"
import Badge from "./ui/Badge"
import Button from "./ui/Button"
import Card from "./ui/Card"
import styles from './HomeProfilePureCSS.module.css';
//import api from 'js-cookie';
import api from '../api/api.js';


const HomeUserProfilePureCSS = ({ isOpen, onClose, userId }) => {
  const [activeTab, setActiveTab] = useState("overview");
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRendered, setIsRendered] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsRendered(true);
    } else {
      const timer = setTimeout(() => setIsRendered(false), 300); // Match animation duration
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && userId) {
      const fetchUserProfile = async () => {
        setLoading(true);
        setError(null);
        try {
          //const response = await fetch(`${API_BASE_URL}/api/HomeUserProfile/getPublicUserProfile?userId=${userId}`);
          const response = await api.get(`/api/HomeUserProfile/getPublicUserProfile?userId=${userId}`);

          //if (!response.ok) {
          //  throw new Error('Network response was not ok');
          //}
          //const data = await response.json();
          setUser(response.data);
        } catch (error) {
          setError(error.message);
        } finally {
          setLoading(false);
        }
      };

      fetchUserProfile();
    }
  }, [isOpen, userId]);

  if (!isRendered) return null;

  if (loading) {
    return (
      <div className={styles.profileModalOverlay}>
        <div className={styles.profileModal}>
          <div className={styles.profileModalContent}>Loading...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.profileModalOverlay}>
        <div className={styles.profileModal}>
          <div className={styles.profileModalContent}>Error: {error}</div>
        </div>
      </div>
    );
  }

  if (!user) return null;

  const renderStars = (rating) => {
    const numRating = Number(rating) || 0;
    return [...Array(5)].map((_, i) => (
      <Star
        key={i}
        size={16}
        className={styles.starIcon}
        style={{
          color: i < Math.round(numRating) ? '#F59E0B' : '#d1d5db',
          fill: i < Math.round(numRating) ? '#F59E0B' : 'none',
        }}
      />
    ))
  }

  return (
    <div className={styles.profileModalOverlay} onClick={onClose}>
      <div 
        className={`${styles.profileModal} ${!isOpen ? styles.hidden : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.profileModalHeader}>
          <h2 className={styles.profileModalTitle}>User Profile</h2>
          <Button variant="ghost" size="sm" onClick={onClose} className={styles.profileCloseBtn}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className={styles.profileModalContent}>
          {/* Profile Header */}
          <div className={styles.profileHeader}>
            <div className={styles.profileAvatarSection}>
              <div className={styles.profileAvatarContainer}>
                <Avatar 
                  className={styles.profileAvatar}
                  src={user.profilePic || "/placeholder.svg"}
                  fallback={`${user.firstName.charAt(0)}${user.lastNameInitial}`}
                />
                <div className={styles.profileAvatarOverlay}>
                  <Camera className="w-4 h-4" />
                </div>
              </div>
            </div>

            <div className={styles.profileInfo}>
              <div className={styles.profileNameSection}>
                <h3 className={styles.profileName}>
                  {user.firstName} {user.lastNameInitial}
                </h3>
                <div className={styles.profileVerificationBadges}>
                  {user.isIdVerified && (
                    <Badge className={`${styles.verificationBadge} ${styles.verified}`}>
                      <Shield className="w-3 h-3 mr-1" />
                      ID Verified
                    </Badge>
                  )}
                  {user.isPaymentVerified && (
                    <Badge className={`${styles.verificationBadge} ${styles.payment}`}>
                      <CreditCard className="w-3 h-3 mr-1" />
                      Payment Verified
                    </Badge>
                  )}
                </div>
              </div>

              <div className={styles.profileMeta}>
                <div className={styles.profileMetaItem}>
                  <MapPin className="w-4 h-4 text-gray-500" />
                  <span>{user.location}</span>
                </div>
                <div className={styles.profileMetaItem}>
                  <Calendar className="w-4 h-4 text-gray-500" />
                  <span>Joined {user.memberSince}</span>
                </div>
              </div>



              <div className={styles.profileMeta}>
                <div className={styles.profileMetaItem}>
                  <PhoneCall className="w-4 h-4 text-gray-500" />
                  <span>{user.phone}</span>
                </div>
                <div className={styles.profileMetaItem}>
                  <Mail className="w-4 h-4 text-gray-500" />
                  <span> {user.email}</span>
                </div>
              </div>




              <div className={styles.profileRating}>
                {user.reviewsReceived > 0 ? (
                  <>
                    <div className={styles.ratingStars}>{renderStars(user.averageRatingGiven)}</div>
                    <span className={styles.ratingText}>
                      {user.averageRatingGiven} ({user.reviewsReceived} {user.reviewsReceived === 1 ? 'review' : 'reviews'})
                    </span>
                  </>
                ) : (
                  <span className={styles.newBadge}>New Member — No reviews yet</span>
                )}
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className={styles.profileStatsGrid}>
            <div className={styles.profileStatCard}>
              <div className={`${styles.statIcon} ${styles.jobs}`}>
                <Briefcase className="w-5 h-5" />
              </div>
              <div className={styles.statContent}>
                <div className={styles.statNumber}>{user.jobsPosted}</div>
                <div className={styles.statLabel}>Jobs Posted</div>
              </div>
            </div>

            <div className={styles.profileStatCard}>
              <div className={`${styles.statIcon} ${styles.hireRate}`}>
                <TrendingUp className="w-5 h-5" />
              </div>
              <div className={styles.statContent}>
                <div className={styles.statNumber}>{user.hireRate}%</div>
                <div className={styles.statLabel}>Hire Rate</div>
              </div>
            </div>

            <div className={styles.profileStatCard}>
              <div className={`${styles.statIcon} ${styles.reviews}`}>
                <Star className="w-5 h-5" />
              </div>
              <div className={styles.statContent}>
                <div className={styles.statNumber}>{user.reviewsReceived}</div>
                <div className={styles.statLabel}>Reviews</div>
              </div>
            </div>

            <div className={styles.profileStatCard}>
              <div className={`${styles.statIcon} ${styles.rating}`}>
                <Award className="w-5 h-5" />
              </div>
              <div className={styles.statContent}>
                <div className={styles.statNumber}>{user.averageRatingGiven}</div>
                <div className={styles.statLabel}>Avg Rating</div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className={styles.profileTabs}>
            <div className={styles.profileTabsList}>
              <button
                className={`${styles.profileTab} ${activeTab === "overview" ? styles.profileTabActive : ""}`}
                onClick={() => setActiveTab("overview")}
              >
                Overview
              </button>
              <button
                className={`${styles.profileTab} ${activeTab === "reviews" ? styles.profileTabActive : ""}`}
                onClick={() => setActiveTab("reviews")}
              >
                Reviews ({user.reviewsReceived})
              </button>
              <button
                className={`${styles.profileTab} ${activeTab === "jobs" ? styles.profileTabActive : ""}`}
                onClick={() => setActiveTab("jobs")}
              >
                Recent Jobs
              </button>
            </div>

            <div className={styles.profileTabsContent}>
              {activeTab === "overview" && (
                <div className={styles.profileOverview}>
                  <div className={styles.overviewCard}>
                    <div className={styles.overviewTitle}>Platform Activity</div>
                    <div className={styles.activityStats}>
                      <div className={styles.activityStat}>
                        <div className={styles.activityStatLabel}>Total Jobs Posted</div>
                        <div className={styles.activityStatValue}>{user.jobsPosted}</div>
                      </div>
                      <div className={styles.activityStat}>
                        <div className={styles.activityStatLabel}>Successful Hires</div>
                        <div className={styles.activityStatValue}>
                          {Math.round((user.jobsPosted * user.hireRate) / 100)}
                        </div>
                      </div>
                      <div className={styles.activityStat}>
                        <div className={styles.activityStatLabel}>Hire Success Rate</div>
                        <div className={styles.activityStatValue}>{user.hireRate}%</div>
                      </div>
                      <div className={styles.activityStat}>
                        <div className={styles.activityStatLabel}>Average Rating</div>
                        <div className={styles.activityStatValue}>{user.averageRatingGiven}/5</div>
                      </div>
                    </div>
                  </div>

                  <div className={styles.overviewCard}>
                    <div className={styles.overviewTitle}>Verification Status</div>
                    <div className={styles.verificationList}>
                      <div className={styles.verificationItem}>
                        <div className={`${styles.verificationIcon} ${styles.verified}`}>
                          <CheckCircle className="w-5 h-5" />
                        </div>
                        <div className={styles.verificationContent}>
                          <div className={styles.verificationTitle}>Identity Verified</div>
                          <div className={styles.verificationDescription}>
                            Identity has been confirmed with the platform
                          </div>
                        </div>
                      </div>
                      <div className={styles.verificationItem}>
                        <div className={`${styles.verificationIcon} ${styles.verified}`}>
                          <CheckCircle className="w-5 h-5" />
                        </div>
                        <div className={styles.verificationContent}>
                          <div className={styles.verificationTitle}>Payment Method Verified</div>
                          <div className={styles.verificationDescription}>Valid payment method on file</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "reviews" && (
                <div className={styles.profileReviews}>
                  <div className={styles.reviewsHeader}>
                    <h4 className={styles.reviewsTitle}>Reviews from Businesses</h4>
                    <p className={styles.reviewsSubtitle}>
                      Feedback from professionals you've worked with on Build Start
                    </p>
                  </div>

                  <div className={styles.reviewsList}>
                    {user.reviewsOfThisUser.map((review) => (
                      <Card key={review.id} className={styles.reviewCard}>
                        <div className={styles.reviewContent}>
                          <div className={styles.reviewHeader}>
                            <div className={styles.reviewBusiness}>
                              <Avatar 
                                className={styles.reviewAvatar}
                                src={review.businessAvatar || "/placeholder.svg"}
                                fallback={review.businessName.charAt(0)}
                              />
                              <div className={styles.reviewBusinessInfo}>
                                <div className={styles.reviewBusinessName}>
                                  {review.businessName}
                                  {review.verified && (
                                    <CheckCircle className="w-4 h-4 text-green-500 ml-1" />
                                  )}
                                </div>
                                <div className={styles.reviewProjectType}>{review.projectType}</div>
                              </div>
                            </div>
                            <div className={styles.reviewMeta}>
                              <div className={styles.reviewRating}>{renderStars(review.rating)}</div>
                              <div className={styles.reviewDate}>{review.date}</div>
                            </div>
                          </div>
                          <div className={styles.reviewComment}>"{review.comment}"</div>
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === "jobs" && (
                <div className={styles.profileJobs}>
                  <div className={styles.jobsHeader}>
                    <h4 className={styles.jobsTitle}>Recent Job Posts</h4>
                    <p className={styles.jobsSubtitle}>Your latest project postings on Build Start</p>
                  </div>

                  <div className={styles.jobsList}>
                    {/* This part needs to be adapted to the new data structure if recent jobs are available in the API response */}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default HomeUserProfilePureCSS
